import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Button, Divider, Drawer, IconButton, Stack, TextField, Typography, Collapse, Chip, Avatar, CircularProgress, Fade, alpha } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CodeIcon from '@mui/icons-material/Code';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Message } from '../../types/copilot';
import { useCopilot } from '../../hooks/useCopilot';
import { useBranding } from '../../contexts/BrandingContext';

function CopilotPanel({ open, onClose, system, tools }: { open: boolean; onClose: () => void; system: string; tools: string[] }) {
  const branding = useBranding();
  const enabled = !!branding.features?.enableCopilot;
  const { messages, send, regenerate, clear, streaming } = useCopilot({ system, tools });
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (messages.length > 0) {
      queueMicrotask(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }));
    }
  }, [messages.length]);

  const onSend = () => {
    const v = input.trim();
    if (!v) return;
    setInput('');
    send(v);
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const toggleToolExpanded = (key: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (!enabled) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          top: { xs: 56, sm: 64 },
          height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
          bgcolor: 'background.default',
        }
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
            <SmartToyIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>Copilot</Typography>
            <Typography variant="caption" color="text.secondary">AI Assistant</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 3, bgcolor: 'background.default' }}>
        {messages.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, color: 'text.secondary' }}>
            <SmartToyIcon sx={{ fontSize: 64, opacity: 0.3 }} />
            <Typography variant="body2" align="center">
              Start a conversation with your AI assistant
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {messages.map((m: Message, idx: number) => {
              // Find tool name for tool messages
              let toolName: string | undefined;
              if (m.role === 'tool' && m.tool_call_id) {
                for (let j = idx - 1; j >= 0; j--) {
                  const prev = messages[j];
                  if (prev.role === 'assistant' && prev.tool_calls?.length) {
                    const found = prev.tool_calls.find((tc: any) => (tc as any).id === m.tool_call_id);
                    if (found) { toolName = found.function?.name || undefined; break; }
                  }
                }
              }

              const isUser = m.role === 'user';
              const isAssistant = m.role === 'assistant';
              const isTool = m.role === 'tool';

              // Render tool call with collapse functionality
              const renderToolCall = (t: any, callIdx: number) => {
                const nm = t?.function?.name || '<pending>';
                const argRaw = t?.function?.arguments || '';
                let pretty = argRaw;
                try { pretty = JSON.stringify(JSON.parse(argRaw), null, 2); } catch {}
                const toolKey = `${idx}-${callIdx}`;
                const isExpanded = expandedTools.has(toolKey);

                return (
                  <Box key={callIdx} sx={{ mb: 1, bgcolor: alpha('#000', 0.03), borderRadius: 1.5, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
                    <Box
                      onClick={() => toggleToolExpanded(toolKey)}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: alpha('#000', 0.02) } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BuildIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace', color: 'warning.dark' }}>
                          {nm}
                        </Typography>
                      </Box>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                    <Collapse in={isExpanded}>
                      <Box sx={{ px: 1.5, pb: 1.5 }}>
                        {pretty && (
                          <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5, border: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.6, display: 'block', color: 'text.primary' }}>
                              {pretty}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              };

              return (
                <Fade in key={idx} timeout={300}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexDirection: isUser ? 'row-reverse' : 'row' }}>
                    {/* Avatar */}
                    <Avatar sx={{ width: 32, height: 32, bgcolor: isUser ? 'primary.main' : isTool ? 'success.main' : 'secondary.main', flexShrink: 0 }}>
                      {isUser ? <PersonIcon fontSize="small" /> : isTool ? <CheckCircleIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                    </Avatar>

                    {/* Message Content */}
                    <Box sx={{ maxWidth: '75%', minWidth: 0 }}>
                      {/* Label */}
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: 'text.secondary', px: 0.5 }}>
                        {isUser ? 'You' : isTool ? (toolName ? `Tool: ${toolName}` : 'Tool Result') : 'Assistant'}
                      </Typography>

                      {/* Main Message Bubble */}
                      <Box sx={{ bgcolor: isUser ? 'primary.main' : 'background.paper', color: isUser ? 'primary.contrastText' : 'text.primary', borderRadius: 2, px: 2, py: 1.5, border: isUser ? 0 : 1, borderColor: 'divider', boxShadow: isUser ? 1 : 0 }}>
                        {/* Reasoning Content */}
                        {m.reasoning_content && (
                          <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
                            <PsychologyIcon sx={{ fontSize: 18, color: 'info.main', flexShrink: 0, mt: 0.25 }} />
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.5 }}>
                              {m.reasoning_content}
                            </Typography>
                          </Box>
                        )}

                        {/* Tool Calls */}
                        {m.tool_calls && m.tool_calls.length > 0 && (
                          <Box sx={{ mb: m.content ? 1.5 : 0 }}>
                            {m.tool_calls.map((t, i) => renderToolCall(t, i))}
                          </Box>
                        )}

                        {/* Main Content */}
                        {m.content && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>
                            {m.content}
                          </Typography>
                        )}

                        {/* Copy Button */}
                        {m.content && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => copy(m.content)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 }, color: isUser ? 'inherit' : 'text.secondary' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Fade>
              );
            })}

            {/* Streaming Indicator */}
            {streaming && (
              <Fade in timeout={300}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', flexShrink: 0 }}>
                    <SmartToyIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ maxWidth: '75%' }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: 'text.secondary', px: 0.5 }}>
                      Assistant
                    </Typography>
                    <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, px: 2, py: 1.5, border: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Thinking...
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            )}
            <div ref={endRef} />
          </Stack>
        )}
      </Box>

      {/* Input Area */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {/* Input Field */}
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask the copilot anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
              }}
              disabled={streaming}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.default' } }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={onSend}
                    disabled={!input.trim() || streaming}
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    <SendIcon />
                  </IconButton>
                )
              }}
            />

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={regenerate}
                disabled={streaming || !messages.some(m => m.role === 'user')}
                sx={{ textTransform: 'none' }}
              >
                Regenerate
              </Button>
              <Button
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={clear}
                disabled={streaming || messages.length === 0}
                color="error"
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}

export default CopilotPanel;
